import { cn } from "@/lib/utils";

interface ScoreGaugeProps {
  score: number;
  className?: string;
}

/**
 * Visual gauge showing where a MIPS score falls across penalty/bonus zones
 *
 * Zones (payment-adjustment semantics):
 * - 0-18: Maximum Penalty (solid red) — fixed -9%
 * - 19-74: Graduated Penalty (red → amber) — linear toward 0%
 * - 75: Neutral — 0%
 * - 76-100: Bonus Zone (amber → green) — linear incentive
 */
export function ScoreGauge({ score, className }: ScoreGaugeProps) {
  const clampedScore = Math.min(100, Math.max(0, score));
  const position = clampedScore; // 0-100 maps directly to percentage

  const zone =
    clampedScore <= 18
      ? "max-penalty"
      : clampedScore <= 74
        ? "penalty"
        : clampedScore === 75
          ? "neutral"
          : "bonus";

  const zoneLabel =
    zone === "max-penalty"
      ? "Maximum Penalty Zone"
      : zone === "penalty"
        ? "Penalty Zone"
        : zone === "neutral"
          ? "Neutral"
          : "Bonus Zone";

  return (
    <div className={cn("w-full", className)}>
      {/* Zone Labels */}
      <div className="flex justify-between text-xs text-muted-foreground mb-1">
        <span>Penalty Zone</span>
        <span>Bonus Zone</span>
      </div>

      {/* Gauge Bar — hard zone segments matching payment buckets */}
      <div className="relative h-8 rounded-full overflow-hidden">
        <div className="absolute inset-0 flex">
          {/* Max Penalty (0–18): solid red */}
          <div className="w-[18%] bg-red-600" />
          {/* Graduated Penalty (19–74): red → amber; ends at the 75 boundary */}
          <div className="w-[57%] bg-gradient-to-r from-red-500 via-orange-500 to-amber-400" />
          {/* Bonus (75–100): light green → dark green so the band reads as a ramp */}
          <div className="w-[25%] bg-gradient-to-r from-lime-300 via-emerald-400 to-emerald-800" />
        </div>

        {/* Zone boundary markers */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 bottom-0 left-[18%] w-px bg-white/60" />
          <div className="absolute top-0 bottom-0 left-[75%] w-0.5 bg-white/80" />
        </div>

        {/* Score Marker */}
        <div
          className="absolute top-0 bottom-0 w-1 bg-white shadow-lg transform -translate-x-1/2 transition-all duration-300 z-10"
          style={{ left: `${position}%` }}
        >
          <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-white rounded-full shadow-md border-2 border-slate-700" />
          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-white rounded-full shadow-md border-2 border-slate-700" />
        </div>
      </div>

      {/* Score Labels */}
      <div className="relative h-6 mt-1">
        <span className="absolute left-0 text-xs text-muted-foreground">0</span>
        <span className="absolute left-[18%] -translate-x-1/2 text-xs text-muted-foreground">18</span>
        <span className="absolute left-[75%] -translate-x-1/2 text-xs font-medium">75</span>
        <span className="absolute right-0 text-xs text-muted-foreground">100</span>

        {/* Current Score Indicator */}
        <div
          className="absolute -translate-x-1/2 transition-all duration-300"
          style={{ left: `${position}%` }}
        >
          <div
            className={cn(
              "px-2 py-0.5 rounded text-xs font-bold text-white shadow-md",
              zone === "max-penalty" && "bg-red-600",
              zone === "penalty" && "bg-amber-500",
              zone === "neutral" && "bg-slate-500",
              zone === "bonus" && "bg-emerald-500"
            )}
          >
            {Math.round(clampedScore)}
          </div>
        </div>
      </div>

      {/* Zone Description */}
      <div className="flex justify-center mt-2">
        <span
          className={cn(
            "text-sm font-medium px-3 py-1 rounded-full",
            zone === "max-penalty" &&
              "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
            zone === "penalty" &&
              "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
            zone === "neutral" &&
              "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
            zone === "bonus" &&
              "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
          )}
        >
          {zoneLabel}
        </span>
      </div>
    </div>
  );
}
